import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OpenAIBaseService } from './open-ai-base.service';
import { DocumentValidationService } from './document-validation.service';
import { SupabaseService } from './supabase.service';
import { AuthService } from './auth.service';
import { Job } from '../types/job.types';

declare const posthog: any;

export interface ResumeAnalysisResult {
  // Job context
  jobTitle?: string;
  company?: string;
  
  // Scores
  atsScore: number;
  keywordMatchScore: number;
  experienceMatchScore: number;
  skillsMatchScore: number;
  
  // Overview with highlights
  overview?: string; // Contains <highlight> tags for highlighted phrases
  scoringMethodology?: string;
  
  // Stats (optional for backward compatibility)
  stats?: {
    actionRequiredCount: number;
    passedChecksCount: number;
    experiencesMatchPercent: number;
    skillsMatchPercent: number;
    experiencesMatched?: number;
    experiencesTotal?: number;
    skillsMatched?: number;
    skillsTotal?: number;
  };
  
  // Positive points (optional for backward compatibility)
  doingWell?: string[]; // Max 8 items
  
  // Issues and tips (optional for backward compatibility)
  actionRequired?: ActionItem[]; // Max 6 items (high + medium priority)
  additionalTips?: TipItem[]; // Max 6 items (low priority)
  
  // Legacy fields for backward compatibility
  analysisSummary?: string;
  improvementTips?: ImprovementTip[];
  missingKeywords?: KeywordMatch[];
  matchingKeywords?: KeywordMatch[];
  
  // Metadata
  resumeSource: 'profile' | 'uploaded';
  resumeFileName?: string;
  job?: any;
  createdAt?: string;
}

export interface ActionItem {
  category: 'Keywords' | 'Experience' | 'Skills' | 'Formatting' | 'Achievements';
  priority: 'high' | 'medium';
  description: string;
  suggestion: string;
}

export interface TipItem {
  category: string;
  priority: 'low';
  description: string;
  suggestion: string;
}

// Legacy interfaces for backward compatibility
export interface ImprovementTip {
  category: string;
  tip: string;
  priority: 'high' | 'medium' | 'low';
  resumeSection?: string;
  specificSuggestion?: string;
}

export interface KeywordMatch {
  keyword: string;
  resumeSection?: string;
  category?: string;
}

/**
 * Helper functions for resume analysis
 */
export class ResumeAnalysisHelpers {
  /**
   * Get score label and emoji based on score
   */
  static getScoreLabel(score: number): { label: string; emoji: string } {
    if (score >= 90) return { label: 'Excellent match', emoji: '🎯' };
    if (score >= 75) return { label: 'Strong match', emoji: '💪' };
    if (score >= 60) return { label: 'Good start', emoji: '👍' };
    if (score >= 40) return { label: 'Getting there', emoji: '🔄' };
    return { label: 'Needs work', emoji: '😟' };
  }

  /**
   * Text colour for score numerals on light UI — same breakpoints as
   * {@link getScoreLabel} (90 / 75 / 60 / 40).
   */
  static getScoreTextColorClass(score: number): string {
    if (score >= 90) {
      return 'text-emerald-600 dark:text-emerald-400';
    }
    if (score >= 75) {
      return 'text-green-600 dark:text-green-400';
    }
    if (score >= 60) {
      return 'text-yellow-600 dark:text-yellow-400';
    }
    if (score >= 40) {
      return 'text-orange-600 dark:text-orange-400';
    }
    return 'text-red-600 dark:text-red-400';
  }

  /**
   * Text colour for score on dark tooltips (higher contrast).
   */
  static getScoreOnDarkTextClass(score: number): string {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 75) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-orange-400';
    return 'text-red-400';
  }

  /** Panel background tiers aligned with {@link getScoreLabel}. */
  static getScorePanelBgClass(score: number): string {
    if (score >= 90) return 'bg-emerald-100 dark:bg-emerald-900';
    if (score >= 75) return 'bg-green-100 dark:bg-green-900';
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900';
    if (score >= 40) return 'bg-orange-100 dark:bg-orange-900';
    return 'bg-red-100 dark:bg-red-900';
  }

  /**
   * Get subtitle for experience/skills match percentage
   */
  static getMatchSubtitle(percent: number): string {
    if (percent >= 90) return 'Excellent match';
    if (percent >= 75) return 'Strong alignment';
    if (percent >= 50) return 'Partial match';
    return 'Needs improvement';
  }

  /**
   * Parse overview text to extract highlights
   * Input: "Your resume shows <highlight>strong experience</highlight>"
   * Output: Array of text segments with highlight flags
   */
  static parseHighlights(text: string): Array<{ text: string; highlight: boolean }> {
    const segments: Array<{ text: string; highlight: boolean }> = [];
    const regex = /<highlight>(.*?)<\/highlight>/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      // Add text before highlight
      if (match.index > lastIndex) {
        segments.push({
          text: text.substring(lastIndex, match.index),
          highlight: false
        });
      }
      // Add highlighted text
      segments.push({
        text: match[1],
        highlight: true
      });
      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      segments.push({
        text: text.substring(lastIndex),
        highlight: false
      });
    }

    return segments;
  }
}

/**
 * Service for analyzing resumes against job descriptions using AI
 * Provides ATS compatibility scoring and improvement recommendations
 */
@Injectable({
  providedIn: 'root',
})
export class ResumeJobAnalyzerService extends OpenAIBaseService {
  private readonly pdfWorkerSrc = '/assets/pdfjs/pdf.worker.min.mjs';

  constructor(
    protected override http: HttpClient,
    private documentValidationService: DocumentValidationService,
    private supabaseService: SupabaseService,
    private authService: AuthService
  ) {
    super();
  }

  /**
   * Analyze user's profile resume against a job
   * @param jobId - Job ID to analyze against
   * @param resumeText - Resume text from user profile
   * @returns Promise with analysis results
   */
  async analyzeResumeAgainstJob(
    jobId: string,
    resumeText: string
  ): Promise<ResumeAnalysisResult> {
    // Validate document is a resume
    const validationResult = await this.documentValidationService.validateIsResume(resumeText);
    
    if (!validationResult.isValid) {
      const errorMessage = this.documentValidationService.getValidationErrorMessage(validationResult);
      throw new Error(errorMessage);
    }

    // Get the original filename from user profile
    const userId = this.authService.getCurrentUser()?.id;
    let resumeFileName: string | undefined;
    
    if (userId) {
      try {
        const { data } = await this.supabaseService
          .getClient()
          .from('users')
          .select('resume_original_filename')
          .eq('id', userId)
          .single();
        
        resumeFileName = data?.resume_original_filename;
      } catch (error) {
        console.warn('Could not fetch resume filename:', error);
      }
    }

    // Fetch job details
    const job = await this.fetchJobDetails(jobId);
    
    // Perform AI analysis
    const analysisResult = await this.performAnalysis(resumeText, job);
    
    // Save analysis to database with filename
    await this.saveAnalysis(jobId, analysisResult, 'profile', resumeFileName);
    
    return {
      ...analysisResult,
      resumeSource: 'profile',
      resumeFileName,
    };
  }

  /**
   * Analyze an uploaded resume file against a job
   * @param jobId - Job ID to analyze against
   * @param file - Uploaded PDF file
   * @returns Promise with analysis results
   */
  async analyzeUploadedResumeAgainstJob(
    jobId: string,
    file: File
  ): Promise<ResumeAnalysisResult> {
    // Validate file is a resume (PDF only)
    const validationResult = await this.documentValidationService.validateResumeFile(file);
    
    if (!validationResult.isValid) {
      const errorMessage = this.documentValidationService.getValidationErrorMessage(validationResult);
      throw new Error(errorMessage);
    }

    // Extract text from PDF (already done in validation, but we need it again)
    const resumeText = await this.extractTextFromFile(file);
    
    // Upload resume to storage and update user profile
    await this.uploadAndUpdateUserResume(file, resumeText);
    
    // Fetch job details
    const job = await this.fetchJobDetails(jobId);
    
    // Perform AI analysis
    const analysisResult = await this.performAnalysis(resumeText, job);
    
    // Save analysis to database
    await this.saveAnalysis(jobId, analysisResult, 'uploaded', file.name);
    
    return {
      ...analysisResult,
      resumeSource: 'uploaded',
      resumeFileName: file.name,
    };
  }

  /**
   * Extract text from PDF file
   */
  private async extractTextFromFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const arrayBuffer = e.target?.result as ArrayBuffer;
          const pdfjsLib = await import('pdfjs-dist');
          pdfjsLib.GlobalWorkerOptions.workerSrc = this.pdfWorkerSrc;
          
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          let fullText = '';
          
          for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const textContent = await page.getTextContent();
            const pageText = textContent.items.map((item: any) => item.str).join(' ');
            fullText += pageText + '\n';
          }
          
          resolve(fullText.trim());
        } catch (error) {
          reject(new Error('Failed to extract text from PDF'));
        }
      };
      
      reader.onerror = () => reject(new Error('Failed to read PDF file'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Upload resume to storage and update user profile
   */
  private async uploadAndUpdateUserResume(file: File, resumeText: string): Promise<void> {
    const userId = this.authService.getCurrentUser()?.id;
    if (!userId) {
      console.warn('User not authenticated, skipping resume upload');
      return;
    }

    try {
      // Generate file path
      const timestamp = Date.now();
      const filePath = `${userId}/${timestamp}_${file.name}`;

      // Upload to Supabase storage
      const { error: uploadError } = await this.supabaseService
        .getClient()
        .storage
        .from('resumes')
        .upload(filePath, file, {
          contentType: 'application/pdf',
          upsert: true,
        });

      if (uploadError) {
        console.error('Error uploading resume to storage:', uploadError);
        // Continue anyway - we can still update the text
      }

      // Get public URL
      const { data: urlData } = this.supabaseService
        .getClient()
        .storage
        .from('resumes')
        .getPublicUrl(filePath);

      // Update user profile with resume text, URL, and original filename
      const { error: updateError } = await this.supabaseService
        .getClient()
        .from('users')
        .update({
          resume_text: resumeText,
          resume_url: urlData.publicUrl,
          resume_original_filename: file.name,
        })
        .eq('id', userId);

      if (updateError) {
        console.error('Error updating user profile with resume:', updateError);
      }
    } catch (error) {
      console.error('Error in uploadAndUpdateUserResume:', error);
      // Don't throw - analysis can still proceed
    }
  }

  /**
   * Fetch job details from database
   */
  private async fetchJobDetails(jobId: string): Promise<Job> {
    const { data, error } = await this.supabaseService
      .getClient()
      .from('jobs')
      .select('*')
      .eq('id', jobId)
      .single();

    if (error || !data) {
      throw new Error('Failed to fetch job details');
    }

    return data as Job;
  }

  /**
   * Perform AI analysis of resume against job
   */
  private async performAnalysis(
    resumeText: string,
    job: Job
  ): Promise<Omit<ResumeAnalysisResult, 'resumeSource' | 'resumeFileName'>> {
    const systemPrompt = `You are an expert ATS (Applicant Tracking System) analyzer and career coach. Your job is to analyze a resume against a job description and provide detailed, actionable feedback in a structured format.

SCORING METHODOLOGY:
- Calculate keyword match % as: (matching keywords / total required keywords) × 100
- Consider semantic equivalents (e.g., "Figma" satisfies "prototyping tools")
- Experience match based on years, relevance, and role similarity
- Skills match based on technical and soft skills alignment
- ATS Score = weighted average: 40% keywords, 30% skills, 30% experience
- ALL SCORES MUST BE WHOLE NUMBERS (integers) - round to nearest whole number, no decimals

IMPORTANT GUIDELINES:
1. Be transparent about how scores are calculated
2. Recognize semantic equivalents (specific tools satisfy general categories)
3. Provide specific, actionable suggestions with exact text to add
4. Prioritize issues: high/medium for critical problems, low for nice-to-haves
5. Highlight positive aspects the candidate is doing well
6. Use <highlight>text</highlight> tags in overview to emphasize key phrases
7. Return ONLY whole numbers for all scores - no decimals like 62.5, use 62 or 63 instead

Return ONLY valid JSON in this exact format:
{
  "jobTitle": "string",
  "company": "string",
  "atsScore": integer (0-100, whole number only),
  "keywordMatchScore": integer (0-100, whole number only),
  "experienceMatchScore": integer (0-100, whole number only),
  "skillsMatchScore": integer (0-100, whole number only),
  "overview": "2-3 sentence summary in first person (e.g., 'You have...', 'Your resume...') with <highlight>key phrases</highlight> highlighted",
  "scoringMethodology": "Brief explanation of how scores were calculated",
  "stats": {
    "actionRequiredCount": number (count of high+medium priority issues),
    "passedChecksCount": number (formatting, structure, basic requirements met),
    "experiencesMatchPercent": number (0-100, same as experienceMatchScore),
    "skillsMatchPercent": number (0-100, same as skillsMatchScore),
    "experiencesMatched": number (REQUIRED - count how many requirements from the job the candidate meets),
    "experiencesTotal": number (REQUIRED - total count of ALL requirements listed in the job description),
    "skillsMatched": number (REQUIRED - count how many required skills the candidate has),
    "skillsTotal": number (REQUIRED - total count of ALL skills listed in the job requirements)
  },
  "doingWell": ["string with <highlight>keywords</highlight>", ...] (max 8 items, positive aspects. Use <highlight> tags to emphasize key skills, tools, or achievements),
  "actionRequired": [
    {
      "category": "Keywords|Experience|Skills|Formatting|Achievements",
      "priority": "high|medium",
      "description": "What's wrong and why it matters. Use <highlight>term</highlight> tags ONLY around missing skills/keywords/requirements, NOT around instruction words like 'emphasize' or 'ensure'.",
      "suggestion": "Exact text or specific action to take. Use <highlight>keyword</highlight> tags to emphasize important keywords or phrases that should be added."
    }
  ] (max 6 items, only high and medium priority. IMPORTANT: Use 'Keywords' for general job-specific terms/buzzwords, 'Skills' for technical abilities/tools, 'Experience' for work history gaps, 'Formatting' for structure issues, 'Achievements' for missing metrics. Avoid duplicate issues - if something fits multiple categories, pick the most specific one.),
  "additionalTips": [
    {
      "category": "string",
      "priority": "low",
      "description": "Optional improvement. Use <highlight>term</highlight> tags ONLY around missing skills/keywords/requirements, NOT around instruction words.",
      "suggestion": "Specific action or text. Use <highlight>keyword</highlight> tags to emphasize important keywords or phrases that should be added."
    }
  ] (max 6 items, only low priority nice-to-haves)
}

LEGACY FIELDS (for backward compatibility, still include these):
{
  "analysisSummary": "same as overview but without highlight tags",
  "improvementTips": [] (empty or minimal),
  "missingKeywords": [] (empty or minimal),
  "matchingKeywords": [] (empty or minimal)
}`;

    const userPrompt = `Analyze this resume against the job description:

JOB TITLE: ${job.title}
COMPANY: ${job.company}
LOCATION: ${job.display_location || job.location}

JOB DESCRIPTION:
${job.short_description || ''}

RESPONSIBILITIES:
${job.responsibilities?.join('\n') || 'Not specified'}

REQUIREMENTS:
${job.requirements?.join('\n') || 'Not specified'}

REQUIRED SKILLS:
${job.skills?.join(', ') || 'Not specified'}

RESUME TEXT:
${resumeText.substring(0, 4000)}

Provide a detailed ATS analysis with specific, actionable improvement tips. 

IMPORTANT: Count ALL requirements listed in the REQUIREMENTS section above. For example, if there are 5 bullet points in requirements, then experiencesTotal should be 5. Count ALL skills listed in REQUIRED SKILLS section for skillsTotal.

Return JSON only.`;

    try {
      const content = await this.makeOpenAIChatRequest(
        userPrompt,
        systemPrompt,
        'gpt-4o-mini', // Cost-effective model for resume analysis
        0.3 // Lower temperature for consistent analysis
      );

      const result = this.parseJsonResponse(content);
      
      // Normalize keywords to new format if they're strings (for backward compatibility)
      const normalizeKeywords = (keywords: any[]): KeywordMatch[] => {
        if (!keywords) return [];
        return keywords.map(k => {
          if (typeof k === 'string') {
            return { keyword: k };
          }
          return k;
        });
      };
      
      // Extract overview text without highlight tags for analysisSummary
      const stripHighlights = (text: string): string => {
        return text.replace(/<highlight>/g, '').replace(/<\/highlight>/g, '');
      };
      
      // Validate and sanitize the response
      return {
        // Job context
        jobTitle: result.jobTitle || job.title,
        company: result.company || job.company,
        
        // Scores (round to whole numbers, no decimals)
        atsScore: Math.round(Math.min(100, Math.max(0, result.atsScore || 0))),
        keywordMatchScore: Math.round(Math.min(100, Math.max(0, result.keywordMatchScore || 0))),
        experienceMatchScore: Math.round(Math.min(100, Math.max(0, result.experienceMatchScore || 0))),
        skillsMatchScore: Math.round(Math.min(100, Math.max(0, result.skillsMatchScore || 0))),
        
        // Overview with highlights
        overview: result.overview || result.analysisSummary || 'Analysis completed',
        scoringMethodology: result.scoringMethodology || 'AI-estimated scores based on keyword match, experience relevance, and skills alignment.',
        
        // Stats (round percentages to whole numbers)
        stats: {
          actionRequiredCount: result.stats?.actionRequiredCount || result.actionRequired?.length || 0,
          passedChecksCount: result.stats?.passedChecksCount || 10,
          experiencesMatchPercent: Math.round(result.stats?.experiencesMatchPercent || result.experienceMatchScore || 0),
          skillsMatchPercent: Math.round(result.stats?.skillsMatchPercent || result.skillsMatchScore || 0),
          experiencesMatched: result.stats?.experiencesMatched,
          experiencesTotal: result.stats?.experiencesTotal,
          skillsMatched: result.stats?.skillsMatched,
          skillsTotal: result.stats?.skillsTotal,
        },
        
        // Positive points
        doingWell: (result.doingWell || []).slice(0, 8),
        
        // Issues and tips
        actionRequired: (result.actionRequired || []).slice(0, 6),
        additionalTips: (result.additionalTips || []).slice(0, 6),
        
        // Legacy fields for backward compatibility
        analysisSummary: result.analysisSummary || stripHighlights(result.overview || 'Analysis completed'),
        improvementTips: (result.improvementTips || []).slice(0, 5),
        missingKeywords: normalizeKeywords((result.missingKeywords || []).slice(0, 10)),
        matchingKeywords: normalizeKeywords((result.matchingKeywords || []).slice(0, 10)),
      };
    } catch (error) {
      console.error('Error performing AI analysis:', error);
      throw new Error('Failed to analyze resume. Please try again.');
    }
  }

  /**
   * Get all resume analyses for the current user
   * @returns Promise with array of analysis results with job details
   */
  async getUserAnalyses(
    userId?: string
  ): Promise<ResumeAnalysisResult[]> {
    const resolvedUserId =
      userId || this.authService.getCurrentUser()?.id;

    if (!resolvedUserId) {
      return [];
    }

    try {
      const { data, error } = await this.supabaseService
        .getClient()
        .from('resume_analyses')
        .select(`
          *,
          job:jobs(*)
        `)
        .eq('user_id', resolvedUserId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user analyses:', error);
        return [];
      }

      return (data || []).map((analysis: any) => ({
        // Job context
        jobTitle: analysis.job_title || analysis.job?.title,
        company: analysis.company || analysis.job?.company,
        // Core scores
        atsScore: analysis.ats_score,
        keywordMatchScore: analysis.keyword_match_score,
        experienceMatchScore: analysis.experience_match_score,
        skillsMatchScore: analysis.skills_match_score,
        // V2 fields
        overview: analysis.overview || analysis.analysis_summary,
        scoringMethodology: analysis.scoring_methodology || 'AI-estimated scores based on keyword match, experience relevance, and skills alignment.',
        stats: analysis.stats || {
          actionRequiredCount: analysis.action_required?.length || 0,
          passedChecksCount: 10,
          experiencesMatchPercent: analysis.experience_match_score,
          skillsMatchPercent: analysis.skills_match_score,
        },
        doingWell: analysis.doing_well || [],
        actionRequired: analysis.action_required || [],
        additionalTips: analysis.additional_tips || [],
        // Legacy fields
        analysisSummary: analysis.analysis_summary,
        improvementTips: analysis.improvement_tips || [],
        missingKeywords: analysis.missing_keywords || [],
        matchingKeywords: analysis.matching_keywords || [],
        resumeSource: analysis.resume_source as 'profile' | 'uploaded',
        resumeFileName: analysis.resume_file_name,
        job: analysis.job,
        createdAt: analysis.created_at,
      }));
    } catch (error) {
      console.error('Error fetching user analyses:', error);
      return [];
    }
  }

  /**
   * Save analysis results to database
   * Replaces any existing analysis for this user+job combination
   */
  private async saveAnalysis(
    jobId: string,
    result: Omit<ResumeAnalysisResult, 'resumeSource' | 'resumeFileName'>,
    resumeSource: 'profile' | 'uploaded' = 'profile',
    resumeFileName?: string
  ): Promise<void> {
    const userId = this.authService.getCurrentUser()?.id;
    
    if (!userId) {
      console.warn('User not authenticated, skipping analysis save');
      return;
    }

    // Track resume report generation in PostHog FIRST (before save)
    // This ensures tracking happens even if the database save fails
    this.trackResumeReportGenerated(
      jobId, 
      result, 
      resumeSource, 
      userId
    );

    try {
      // First, delete any existing analysis for this user + job
      await this.supabaseService
        .getClient()
        .from('resume_analyses')
        .delete()
        .eq('user_id', userId)
        .eq('job_id', jobId);

      // Then insert the new analysis
      const { error } = await this.supabaseService
        .getClient()
        .from('resume_analyses')
        .insert({
          user_id: userId,
          job_id: jobId,
          // Core scores
          ats_score: result.atsScore,
          keyword_match_score: result.keywordMatchScore,
          experience_match_score: result.experienceMatchScore,
          skills_match_score: result.skillsMatchScore,
          // V2 fields
          job_title: result.jobTitle,
          company: result.company,
          overview: result.overview,
          stats: result.stats,
          doing_well: result.doingWell,
          action_required: result.actionRequired,
          additional_tips: result.additionalTips,
          // Legacy fields
          analysis_summary: result.analysisSummary,
          scoring_methodology: result.scoringMethodology,
          improvement_tips: result.improvementTips,
          missing_keywords: result.missingKeywords,
          matching_keywords: result.matchingKeywords,
          // Metadata
          resume_source: resumeSource,
          resume_file_name: resumeFileName,
        });

      if (error) {
        console.error('Error saving analysis:', error);
        // Don't throw - analysis succeeded, just save failed
      }
    } catch (error) {
      console.error('Error saving analysis to database:', error);
      // Don't throw - analysis succeeded, just save failed
    }
  }

  /**
   * Track resume report generation in PostHog
   */
  private trackResumeReportGenerated(
    jobId: string,
    result: Omit<ResumeAnalysisResult, 'resumeSource' | 'resumeFileName'>,
    resumeSource: 'profile' | 'uploaded',
    userId: string
  ): void {
    try {
      // Try both posthog and window.posthog
      const ph = typeof posthog !== 'undefined' ? posthog : (window as any).posthog;
      
      if (ph && typeof ph.capture === 'function') {
        ph.capture('resume_report_generated', {
          job_id: jobId,
          job_title: result.jobTitle,
          company: result.company,
          ats_score: result.atsScore,
          keyword_match_score: result.keywordMatchScore,
          experience_match_score: result.experienceMatchScore,
          skills_match_score: result.skillsMatchScore,
          resume_source: resumeSource,
          user_id: userId
        });
      }
    } catch (error) {
      console.error('Error tracking resume_report_generated:', error);
    }
  }
}

